import { Dispatch, SetStateAction, useState } from 'react';

import { DownloadIcon, EyeIcon } from '../../../../assets/svg';
import { ETPayButton } from '../../../../components/button';
import { DateAndTime } from '../../../../components/date-and-time';
import { Select } from '../../../../components/form/select';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { Status } from '../../../../components/status';
import { ETPayTable } from '../../../../components/table';
import { useAuth } from '../../../../hooks/useAuth';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { Payment, PaymentStatus, productTypeMap } from '../../../../models/common-payments';
import { ColumnInterface, Filters, Pagination, RowInterface } from '../../../../models/table';
import { AllPaymentsServices } from '../../../../services/all-payments';
import { getRoleLabelById, validatePermission } from '../../../../utils/common';
import { errorFormatter } from '../../../../utils/errors';
import { fillStatesWithZeros } from '../../../../utils/states';
import {
	AmountContainer,
	Content,
	DetailsContainer,
	ExportModalContent,
	ExportModalDescription,
	ExportModalTitle,
	IconContainer,
	ItemData,
	ProductFilterLabel,
	Section,
	SelectContainer,
	StatusContainer,
	TextSub,
	TextTitle,
	Title,
	Value,
	VoucherButton,
	VoucherContainer,
} from './styles';

interface AllPaymentsTableProps {
	data: Payment[];
	isLoading?: boolean;
	stateList: PaymentStatus[];
	filters: Filters;
	onFilterChange: Dispatch<SetStateAction<Filters>>;
	pagination: Pagination;
	setPagination: Dispatch<SetStateAction<Pagination>>;
	totalResults?: number;
}

interface ModalProps {
	loader: boolean;
	show: boolean;
	url: string;
}

const defaultModal: ModalProps = {
	loader: false,
	show: false,
	url: '',
};

const AllPaymentsTable = ({
	data,
	isLoading,
	stateList,
	filters,
	onFilterChange,
	pagination,
	setPagination,
	totalResults,
}: AllPaymentsTableProps) => {
	const { user } = useAuth();
	const { merchantSelected } = useMerchant();
	const [modal, setModal] = useState<ModalProps>(defaultModal);
	const { handleShowAlert } = useFloatingAlert();

	const getColumns = () => {
		const columns: ColumnInterface<Payment>[] = [
			{
				title: 'Referencia',
				propertyName: 'reference',
				cellRender: ({ reference }) => <>{reference}</>,
			},
			{
				title: 'RUT',
				propertyName: 'rut',
				hide: merchantSelected?.country === 'MX',
				cellRender: ({ rut }) => <>{rut !== 'none' ? rut : null}</>,
			},
			{
				title: 'Producto',
				propertyName: 'productType',
				align: 'center',
				cellRender: ({ productType }) => (
					<IconContainer>{productTypeMap.get(productType)}</IconContainer>
				),
			},
			{
				sortField: true,
				title: 'Monto',
				align: 'right',
				propertyName: 'amount',
				cellRender: ({ amount }) => <AmountContainer>{amount}</AmountContainer>,
			},
			{
				sortField: true,
				title: merchantSelected?.country === 'MX' ? 'Cuenta' : 'Banco',
				propertyName: 'bankName',
				cellRender: ({ bankName, userClabe }) => (
					<>
						{bankName === 'DESCONOCIDO' &&
							merchantSelected?.country === 'MX' ? null : merchantSelected?.country === 'MX' ? (
								<ItemData>
									<TextTitle>{userClabe}</TextTitle>
									<TextSub>{bankName}</TextSub>
								</ItemData>
							) : (
							bankName
						)}
					</>
				),
			},
			{
				title: 'Medio de pago',
				propertyName: 'paymentMethod',
				hide: merchantSelected?.country !== 'MX',
				cellRender: ({ paymentMethod }) => <>{paymentMethod}</>,
			},
			{
				title: 'Estatus',
				align: 'center',
				propertyName: 'status',
				cellRender: ({ status }) => (
					<StatusContainer>
						<Status status={status} borderFull />
					</StatusContainer>
				),
			},
			{
				sortField: true,
				title: 'Fecha de creación',
				propertyName: 'createdatDate',
				cellRender: ({ createdatDate, createdatTime }) => (
					<DateAndTime date={createdatDate} time={createdatTime} />
				),
			},
		];

		if (merchantSelected?.provider == 'stp' || merchantSelected?.provider == null) {
			columns.push({
				title: 'Comprobante',
				align: 'center',
				propertyName: 'urlCEP',
				cellRender: ({ validationMailPdf, urlCEP }) => (
					<VoucherContainer>
						{urlCEP ? (
							<VoucherButton onClick={() => window.open(urlCEP)}>
								<EyeIcon />
							</VoucherButton>
						) : validationMailPdf ? (
							<VoucherButton onClick={() => window.open(validationMailPdf)}>
								<DownloadIcon color="#646174" />
							</VoucherButton>
						) : null}
					</VoucherContainer>
				),
			});
		}

		return columns;
	};

	const columns = getColumns();

	const detailSection: RowInterface<Payment> = (data) => {
		const isNotPending = data.status !== 'pending';

		return (
			<DetailsContainer $hasError={!!data.typeError && isNotPending && !data.clabe}>
				{isNotPending && (
					<Section>
						{merchantSelected?.country === 'CL' &&
							data.status === 'success' &&
							data.cantidadCuotas !== undefined && (
								<>
									<Content>
										<Title>Cantidad de cuotas</Title>
										<Value>{data.cantidadCuotas}</Value>
									</Content>
									<Content>
										<Title>Código de autorización</Title>
										<Value>{data.codAutorizacion}</Value>
									</Content>
								</>
							)}

						{merchantSelected?.country === 'MX' &&
							(data.paymentMethod === 'SPEI' || data.paymentMethod === 'Efectivo') &&
							data?.email && (
								<Content>
									<Title>Correo electrónico</Title>
									<Value>{data.email}</Value>
								</Content>
							)}
						{merchantSelected?.country === 'MX' && data.paymentMethod === 'Efectivo' && data.status == 'success' &&
							<>
								<Content>
									<Title>Nombre completo</Title>
									<Value>{data.userName}</Value>
								</Content>
								<Content>
									<Title>Corresponsalia</Title>
									<Value>{data.correspondent}</Value>
								</Content>
							</>}

						{merchantSelected?.country === 'CL' &&
							data.status != 'expired' &&
							data.status != 'error' &&
							data.status != 'success' && (
								<>
									<Content>
										<Title>Tipo de cuenta</Title>
										<Value>{data.accountType}</Value>
									</Content>

									<Content>
										<Title>No. de Cuenta</Title>
										<Value>{data.account}</Value>
									</Content>

									<Content>
										<Title>Paso pendiente de realizar</Title>
										<Value>{data.btstep}</Value>
									</Content>
								</>
							)}

						{merchantSelected?.country === 'MX' && data.paymentMethod === 'CoDi' && (
							<Content>
								<Title>Número de teléfono</Title>
								<Value>{data.phone}</Value>
							</Content>
						)}

						{merchantSelected?.country === 'MX' &&
							data.status === 'expired' &&
							(getRoleLabelById(merchantSelected?.role_id, merchantSelected?.role_id) === 'ETpay' ||
								user?.email.includes('@etpay')) && (
								<Content>
									<Title>Último paso</Title>
									<Value>{data.lastStep}</Value>
								</Content>
							)}
						{data.rut && data.rut != 'none' && (
							<Content>
								<Title>RUT</Title>
								<Value>{data.rut}</Value>
							</Content>
						)}

						{merchantSelected?.country === 'MX' && (
							<Content $hasError={!!data.typeError && isNotPending && !data.clabe}>
								<Title>Fecha de expiración</Title>
								<Value>{data.expiredate}</Value>
							</Content>
						)}
						{data.validatedatDate && merchantSelected?.country === 'MX' && data.paymentMethod != "Efectivo" && (
							<Content>
								<Title>Fecha de validación</Title>
								<Value>
									{data.validatedatDate} {data.validatedatTime}
								</Value>
							</Content>
						)}

						{data.typeError && data.status !== 'expired' && (
							<Content>
								<Title>Tipo de error</Title>
								<Value>{data.typeError}</Value>
							</Content>
						)}

						{merchantSelected?.country !== 'MX' && (
							<Content toEnd $hasError={!!data.typeError && isNotPending && !data.clabe}>
								<Title>Fecha de expiración</Title>
								<Value>{data.expiredate}</Value>
							</Content>
						)}
						{/* {data.status !== 'error' && (
							<Content toEnd $hasError={!!data.typeError && isNotPending && !data.clabe}>
								<Title>Fecha de expiración</Title>
								<Value>{data.expiredate}</Value>
							</Content>
						)} */}
					</Section>
				)}

				{data.status === 'pending' &&
					merchantSelected?.country === 'MX' &&
					(data.email || data.lastStep) && (
						<Section>
							<Content>
								<Title>Correo electronico</Title>
								<Value>{data.email}</Value>
							</Content>

							{data.lastStep &&
								(getRoleLabelById(merchantSelected?.role_id, merchantSelected?.role_id) ===
									'ETpay' ||
									user?.email.includes('@etpay')) && (
									<Content>
										<Title>Último paso</Title>
										<Value>{data.lastStep}</Value>
									</Content>
								)}
						</Section>
					)}

				<Section>
					<Content>
						<Title>{data.status === 'success' ? 'Payment ID' : 'Session Token'}</Title>
						<Value>{data.status === 'success' ? data.paymentId : data.sessionToken}</Value>
					</Content>
					{data.validatedatDate && merchantSelected?.country !== 'MX' && (
						<Content>
							<Title>Fecha de validación</Title>
							<Value>
								{data.validatedatDate} {data.validatedatTime}
							</Value>
						</Content>
					)}
					{/* {data.status === 'error' && (
						<Content toEnd $hasError={!!data.typeError && isNotPending && !data.clabe}>
							<Title>Fecha de expiración</Title>
							<Value>{data.expiredate}</Value>
						</Content>
					)} */}
					{data.validatedatDate && merchantSelected?.country === 'MX' && data.paymentMethod == "Efectivo" && (
						<Content>
							<Title>Fecha de validación</Title>
							<Value>
								{data.validatedatDate} {data.validatedatTime}
							</Value>
						</Content>
					)}
				</Section>

				{data.transferId && (
					<Section>
						<Content>
							<Title>Transfer ID</Title>
							<Value>{data.transferId}</Value>
						</Content>
					</Section>
				)}
			</DetailsContainer>
		);
	};

	const handleExportAction = async () => {
		setModal({ ...modal, loader: true, show: true });

		try {
			const response = await AllPaymentsServices.getExportData({
				merchantCode: merchantSelected?.code as string,
				dateRange: {
					from: filters?.dateRange?.from,
					to: filters?.dateRange?.to,
				},
				search: filters?.search,
				stateSelected: filters?.stateSelected,
			});

			if (response.url) setModal({ loader: false, show: true, url: response.url });
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: errorFormatter(e.code),
			});
		}
	};

	const handleCloseModal = () => {
		setModal(defaultModal);
	};

	const handleDownloadData = () => {
		if (modal.url) window.location.assign(modal.url);
		setModal(defaultModal);
	};

	const renderSelectByType = () => (
		<SelectContainer>
			<Select
				options={[
					{ label: 'Todos', value: '', customLabel: 'Todos', show: true },
					{
						label: (
							<ProductFilterLabel>SPEI
							</ProductFilterLabel>
						),
						value: 'spei',
						customLabel: 'SPEI',
						show: true,
					},
					{
						label: (
							<ProductFilterLabel> CoDi
							</ProductFilterLabel>
						),
						value: 'codi',
						customLabel: 'CoDi',
						show: true,
					},
					{
						label: (
							<ProductFilterLabel> Efectivo
							</ProductFilterLabel>
						),
						value: 'efectivo',
						customLabel: 'Efectivo',
						show: true,
					},
				]}
				prefix="Medio de pago:"
				onChange={({ value }) => {
					if (onFilterChange) {
						onFilterChange({ ...filters, method: value as string });
					}
				}}
			/>
		</SelectContainer>
	);

	return (
		<>
			{modal.show && (
				<Modal
					title="Exportar"
					show={modal.show}
					onClose={handleCloseModal}
					allowCloseModal={!modal.loader}
				>
					{modal.loader ? (
						<Spinner />
					) : (
						<ExportModalContent>
							<ExportModalTitle>Tu reporte está listo</ExportModalTitle>

							<ExportModalDescription>
								Hemos terminado de recopilar los datos de tu reporte. Haz clic en el botón para
								descargarlo.
							</ExportModalDescription>
							<ETPayButton
								title="Descargar reporte"
								variant="effect"
								onClick={handleDownloadData}
							/>
						</ExportModalContent>
					)}
				</Modal>
			)}

			<ETPayTable<Payment>
				title="Todos los cobros"
				data={data}
				isLoading={!!isLoading}
				columns={columns}
				row={detailSection}
				filtersMobil={merchantSelected?.country != 'MX'}
				customFilter={renderSelectByType()}
				hasFilters
				hasDateFilter
				allowExport={validatePermission('ALL_PAYMENTS_EXPORT')}
				stateList={fillStatesWithZeros({ stateList })}
				filters={filters}
				onFilterChange={onFilterChange}
				hasSearch
				exportAction={handleExportAction}
				hasPagination
				allowCustomFilter={merchantSelected?.country == 'MX'}
				totalResults={totalResults}
				pagination={pagination}
				setPagination={setPagination}
			/>
		</>
	);
};

export { AllPaymentsTable };
